import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "../LangContext";
import {
  PAGE_LANG,
  applyGoogleTranslate,
  consumePendingLang,
  loadGoogleTranslateScript,
  triggerLanguageChange,
  writeGoogTransCookie,
} from "../utils/googleSiteTranslate";

/**
 * Motore di traduzione a pagina intera (Google Website Translator).
 * La bandierina in LangContext guida la lingua; qui si applica a TUTTO il DOM,
 * non solo alle chiavi i18n dell'hero/header.
 */
export default function GoogleSiteTranslator() {
  const { lang, langEpoch } = useLang();
  const { pathname } = useLocation();
  const readyRef = useRef(false);
  const pendingRef = useRef(lang);

  useEffect(() => {
    let cancelled = false;
    writeGoogTransCookie(lang);
    loadGoogleTranslateScript()
      .then(() => {
        if (cancelled) return;
        readyRef.current = true;
        // Lingua chiesta prima di un reload (cookie attivo): la riapplichiamo.
        const pending = consumePendingLang();
        if (pending) pendingRef.current = pending;
        // Piccolo delay: il select .goog-te-combo compare dopo l'init
        const tryApply = (attempt = 0) => {
          const ok = applyGoogleTranslate(pendingRef.current || lang);
          if (!ok && attempt < 8) {
            window.setTimeout(() => tryApply(attempt + 1), 200 + attempt * 100);
          }
        };
        tryApply();
      })
      .catch(() => {
        /* rete / script bloccato: resta i18n locale */
      });
    return () => {
      cancelled = true;
    };
    // solo mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    pendingRef.current = lang;
    writeGoogTransCookie(lang);
    if (!readyRef.current) return undefined;
    // triggerLanguageChange ricarica una sola volta se la combo non è pronta
    const id = window.setTimeout(() => triggerLanguageChange(lang), 80);
    return () => window.clearTimeout(id);
  }, [lang, langEpoch]);

  // Nuova rotta SPA: ri-applica la lingua sul DOM aggiornato (forzata:
  // il combo resta già sul target ma i nodi React nuovi sono in italiano)
  useEffect(() => {
    if (!readyRef.current || lang === PAGE_LANG) return undefined;
    const id = window.setTimeout(() => applyGoogleTranslate(lang, { force: true }), 250);
    return () => window.clearTimeout(id);
  }, [pathname, lang]);

  // React sostituisce sottoalberi dopo il pass di traduzione (loading→dati,
  // pin mappa, feed) e l'observer interno di Google non copre sempre le
  // sostituzioni intere: re-apply forzato, debounced e limitato per pagina.
  useEffect(() => {
    if (!readyRef.current || lang === PAGE_LANG) return undefined;
    if (typeof MutationObserver === "undefined" || !document.body) return undefined;
    let debounce = null;
    let applies = 0;
    let lastApply = 0;
    const obs = new MutationObserver((records) => {
      let hasText = false;
      for (const r of records) {
        for (const node of r.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          // Ignora i wrapper inseriti da Google Translate stesso (<font>,
          // gadget, banner) e i nodi marcati notranslate: sono mutazioni
          // generate dalla traduzione, non contenuto React nuovo.
          if (
            node.tagName === "FONT" ||
            node.closest?.("font, .goog-te-combo, .goog-te-banner-frame, .skiptranslate, #google_translate_element, .notranslate")
          ) {
            continue;
          }
          if (String(node.textContent || "").trim().length > 20) {
            hasText = true;
            break;
          }
        }
        if (hasText) break;
      }
      if (!hasText) return;
      if (debounce) window.clearTimeout(debounce);
      debounce = window.setTimeout(() => {
        // Max 6 re-apply per pagina, min 4s l'uno dall'altro: niente loop.
        if (applies >= 6 || Date.now() - lastApply < 4000) return;
        applies += 1;
        lastApply = Date.now();
        applyGoogleTranslate(lang, { force: true });
      }, 900);
    });
    obs.observe(document.body, { childList: true, subtree: true });
    return () => {
      obs.disconnect();
      if (debounce) window.clearTimeout(debounce);
    };
  }, [pathname, lang]);

  return (
    <div
      id="google_translate_element"
      className="google-site-translator"
      aria-hidden
      data-testid="google-site-translator"
    />
  );
}
